import {z} from 'zod';

import {useEffect, useState} from 'react';

import {defaultTo, equals, isNil, not} from 'ramda';

import {Nullish, useDebouncedValue, usePrevious} from 'shared/web';

import {EitherQuantityOrError} from '../types/basket/EitherQuantityOrError';

interface UseBasketItemQuantityProps {
  itemId: string;
  currentQuantity: number | null;
  validationSchema: z.ZodNumber | z.ZodEffects<z.ZodNumber, number, number>;
  onQuantityChange: (itemId: string, newQuantity: EitherQuantityOrError) => void;
  // Basket-wide flag that stays true until the quantity mutation AND the invalidated
  // basket refetch have both settled. Used to reconcile the input with the server value.
  hasPendingRequest: boolean;
}

const DEBOUNCE_DELAY = 1000;

export function useBasketItemQuantity(props: UseBasketItemQuantityProps) {
  const [quantity, setQuantity] = useState<number | null>(defaultTo(null, props.currentQuantity));
  const [quantityError, setQuantityError] = useState<string | Nullish>(null);

  const debouncedQuantity = useDebouncedValue(quantity, DEBOUNCE_DELAY);

  const wasPendingRequest = usePrevious(props.hasPendingRequest);

  // Controls the local changes of quantity value
  useEffect(
    function submitDebouncedQuantity() {
      const hasValidQuantity = validateQuantity(quantity);
      const shouldNotChangeQuantity = not(hasValidQuantity) || isNil(debouncedQuantity);

      if (shouldNotChangeQuantity) {
        return;
      }

      props.onQuantityChange(props.itemId, {
        newQuantity: debouncedQuantity,
        hasPendingQuantityChange: false,
      });
      // We want to handle the debouncedQuantity separately from props.item.quantity
    },
    // eslint-disable-next-line react/exhaustive-deps
    [debouncedQuantity]
  );

  // Controls the changes that happened outside of the basket item quantity input
  // e.g. edit dialog
  useEffect(
    function syncQuantityFromProps() {
      const hasValidQuantity = validateQuantity(props.currentQuantity);
      const shouldQuantityChange = not(equals(props.currentQuantity, debouncedQuantity));

      if (hasValidQuantity && shouldQuantityChange) {
        setQuantity(props.currentQuantity);
      }
      // We want to handle the props.item.quantity separately from debouncedQuantity
    },
    // eslint-disable-next-line react/exhaustive-deps
    [props.currentQuantity]
  );

  // Reconciles the input with the server value once a quantity request settles.
  // This covers the case where the request FAILED but the server value did not change
  // (e.g. user changes 1 -> 2, request fails, invalidated basket GET still returns 1):
  // the external-sync effect above never fires because props.currentQuantity stays the
  // same, so without this the input would keep showing the unsaved value.
  useEffect(
    function resetToServerQuantityOnRequestSettle() {
      // The mutation and the invalidated basket refetch have both settled, so
      // props.currentQuantity now holds the authoritative server value.
      const hasRequestJustSettled = wasPendingRequest === true && not(props.hasPendingRequest);
      // Guard: don't wipe a value the user typed but hasn't flushed through the debounce yet
      // (can happen when a different item's request toggled the basket-wide pending flag).
      const hasNoUnflushedLocalEdit = equals(quantity, debouncedQuantity);
      // Guard: don't snap back a value the user is still correcting (invalid input shows its own error).
      const hasNoValidationError = isNil(quantityError);
      // Only reset when the displayed value actually diverges from the server (e.g. a failed save).
      const hasQuantityDivergedFromServer = not(equals(quantity, props.currentQuantity));

      const shouldResetToServerQuantity =
        hasRequestJustSettled &&
        hasNoUnflushedLocalEdit &&
        hasNoValidationError &&
        hasQuantityDivergedFromServer;

      if (shouldResetToServerQuantity) {
        setQuantity(props.currentQuantity);
      }
      // We only want to react to the pending-request settle edge, not to every dependency change
    },
    // eslint-disable-next-line react/exhaustive-deps
    [props.hasPendingRequest]
  );

  const validateQuantity = (value: number | null) => {
    const valueToValidate = defaultTo(0, value);

    setQuantityError(null);

    const result = props.validationSchema.safeParse(valueToValidate);

    if (result?.success) {
      return true;
    }

    const errorMessages = result?.error?.errors.map((error) => error.message).join(' ');
    setQuantityError(errorMessages);
    return false;
  };

  const handleQuantityChange = (newQuantity: number | null) => {
    const hasValidQuantity = validateQuantity(newQuantity);

    setQuantity(newQuantity);

    // Report the error state and the not-yet-saved state immediately, even though the actual
    // quantity update is debounced, so the basket owner can block committing actions from the
    // first keystroke. Restoring the server value reports no pending change - there is nothing
    // to save, which matches the parent skipping the request for an unchanged quantity.
    props.onQuantityChange(props.itemId, {
      hasError: not(hasValidQuantity),
      hasPendingQuantityChange: not(equals(newQuantity, props.currentQuantity)),
    });
  };

  return {
    quantity,
    quantityError,
    handleQuantityChange,
  };
}
